/**
 * AnnualPaywall
 * ─────────────────────────────────────────────────────────────────────────
 * Step 3 of the public web sign-up flow. We arrive here after the user has
 * created their account (POST /auth/register succeeded and we already have
 * an `access_token` in Redux). The job of this screen is to:
 *
 *   1. Tell the student bluntly that the *web* dashboard is annual-only —
 *      no monthly tier on web. A monthly subscription has to come from
 *      the iOS / Android app, where Apple/Google IAP is available. (We
 *      deliberately surface the "open the app" CTA prominently so a user
 *      who *wants* monthly doesn't feel railroaded into annual.)
 *   2. Trigger the RevenueCat Web Billing checkout for the annual product.
 *      RC handles Stripe under the hood. On success the same RC customer
 *      record (keyed by `user.id`) reflects the entitlement, and the
 *      existing RC → API webhook updates `User.current_subscription` —
 *      so the dashboard's normal subscription gating Just Works after
 *      this step.
 *
 * Why this lives next to sign-up (and not in a dedicated route): the
 * paywall is intentionally non-routable. A user can't bookmark `/paywall`
 * and skip account creation; they always arrive here from the registration
 * stepper, with `appUserId` + `email` already in hand.
 */

import React, { useState } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import {
  isWebBillingConfigured,
  startAnnualCheckout,
} from "../../../../utils/revenueCatWeb";

const APP_STORE_URL = "https://apps.apple.com/app/microlab/id6469486411";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.saeedtkh.microlab";

function AnnualPaywall({
  appUserId,
  email,
  /** Called when the entitlement is granted; the host reloads to enter the dashboard. */
  onSuccess,
}) {
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const configured = isWebBillingConfigured();

  const handleStartCheckout = async () => {
    setErrorMessage(null);
    setBusy(true);
    try {
      const result = await startAnnualCheckout({ appUserId, email });
      if (result.ok) {
        onSuccess?.(result);
        return;
      }
      // Silent on user-cancelled so we don't punish the student for
      // backing out of the Stripe iframe — we just let them try again.
      if (result.code === "CANCELLED") return;
      setErrorMessage(
        result.error?.message ||
          "We couldn't start the checkout. Please try again or contact support."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <MDBox
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        mx={2}
        mt={-3}
        p={3}
        mb={1}
        textAlign="center"
      >
        <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
          Activate your Microlab Annual
        </MDTypography>
        <MDTypography display="block" variant="button" color="white" my={1}>
          One last step before you can study on the web.
        </MDTypography>
      </MDBox>

      <MDBox pt={3} pb={3} px={3}>
        {/*
          We lead with the "annual-only" notice on purpose. Burying it
          under the CTA tends to push users to click first and feel
          tricked when there's no monthly option later. Up front it
          reads as a clear product policy instead.
        */}
        <MDAlert color="warning" dismissible={false}>
          <MDTypography variant="button" color="white" fontWeight="medium">
            The Microlab dashboard is available on annual subscription only.
            For monthly billing, please use the mobile app — App Store / Google
            Play handle billing there directly.
          </MDTypography>
        </MDAlert>

        <Stack spacing={1.5} sx={{ mt: 2.5 }}>
          {[
            "Full access to every slide, quiz, 3D model, and Copilot study pack",
            "Unlimited AI questions and chat history across web + mobile",
            "Same account synced with the iOS / Android app you already use",
            "Cancel anytime — no auto-renew without a clear reminder",
          ].map((line) => (
            <Stack
              key={line}
              direction="row"
              alignItems="flex-start"
              spacing={1}
            >
              <CheckCircleRoundedIcon
                fontSize="small"
                sx={{ color: "success.main", mt: "2px" }}
              />
              <MDTypography variant="button" color="text">
                {line}
              </MDTypography>
            </Stack>
          ))}
        </Stack>

        {/* Primary CTA: annual checkout */}
        <MDBox mt={3}>
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            disabled={!configured || busy}
            onClick={handleStartCheckout}
          >
            {busy ? "Opening secure checkout…" : "Continue with Annual"}
          </MDButton>
          {!configured ? (
            <MDBox mt={1} textAlign="center">
              <MDTypography variant="caption" color="text">
                Web checkout is not configured yet. Set{" "}
                <code>REACT_APP_REVENUECAT_WEB_API_KEY</code> on the build host
                and rebuild the dashboard.
              </MDTypography>
            </MDBox>
          ) : null}
          {errorMessage ? (
            <MDBox mt={1} textAlign="center">
              <MDTypography variant="caption" color="error">
                {errorMessage}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>

        {/* Secondary CTA: send the user to the app for monthly billing.
            Two buttons (App Store + Play) instead of one generic link so
            mobile-on-the-side desktops on either OS get the right deep
            link without us having to UA-sniff. */}
        <MDBox mt={3}>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            textAlign="center"
            mb={1}
          >
            Need monthly billing? Subscribe inside the app, then sign in here.
          </MDTypography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <MDButton
              variant="outlined"
              color="dark"
              fullWidth
              startIcon={<PhoneIphoneRoundedIcon />}
              endIcon={<OpenInNewRoundedIcon />}
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store
            </MDButton>
            <MDButton
              variant="outlined"
              color="dark"
              fullWidth
              startIcon={<PhoneIphoneRoundedIcon />}
              endIcon={<OpenInNewRoundedIcon />}
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Play
            </MDButton>
          </Stack>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default AnnualPaywall;
